<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const slides = [
  {
    title: "Made with care",
    description:
      "Welcome to Secured Cares, your go-to resource for finding reliable and compassionate caregivers. We understand that caring for someone you love can be demanding, and sometimes, extra support is needed.",
    button: "READ MORE",
    image: new URL("../assets/img/home_background_1.jpg", import.meta.url).href,
  },
  {
    title: "Professional Care",
    description:
      "Our experienced caregivers provide dependable support tailored to your family's needs.",
    button: "OUR SERVICES",
    image: new URL("../assets/img/home_background_1cd.jpg", import.meta.url).href,
  },
  {
    title: "Trusted Healthcare",
    description:
      "Quality care with compassion. Helping families find peace of mind every day.",
    button: "CONTACT US",
    image: new URL("../assets/img/home_background_1cd.jpg", import.meta.url).href,
  },
];

const current = ref(0);
const interval = 5000;

let timer = null;

const currentSlide = computed(() => slides[current.value]);

function next() {
  current.value = (current.value + 1) % slides.length;
}

function previous() {
  current.value =
    (current.value - 1 + slides.length) % slides.length;
}

function goTo(index) {
  current.value = index;
}

function startAutoplay() {
  stopAutoplay();
  timer = setInterval(next, interval);
}

function stopAutoplay() {
  if (timer) clearInterval(timer);
}

onMounted(startAutoplay);

onUnmounted(stopAutoplay);
</script>

<template>
  <section
    class="hero"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
  >
    <Transition name="fade" mode="out-in">
      <div class="slide" :key="current">
        <!-- Background Image -->
        <img
          :src="currentSlide.image"
          :alt="currentSlide.title"
          class="bg-image"
        />

        <!-- Gradient Overlay -->
        <div class="overlay"></div>

        <!-- Content -->
        <div class="content">
          <h1>{{ currentSlide.title }}</h1>

          <p>
            {{ currentSlide.description }}
          </p>

          <button>
            {{ currentSlide.button }}
          </button>
        </div>
      </div>
    </Transition>

    <button class="arrow left" @click="previous">
      &#10094;
    </button>

    <button class="arrow right" @click="next">
      &#10095;
    </button>

    <div class="dots">
      <span
        v-for="(slide, index) in slides"
        :key="index"
        :class="{ active: index === current }"
        @click="goTo(index)"
      />
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  width: 100%;
  height: 85vh;
  overflow: hidden;
}

.slide {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
}

/* Background Image */
.bg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  object-fit: cover;
  object-position: center center;

  z-index: 0;
}

/* Gradient Overlay */
.overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.92) 0%,
    rgba(255, 255, 255, 0.75) 35%,
    rgba(255, 255, 255, 0.1) 70%
  );
  z-index: 1;
}

.content {
  position: relative;
  z-index: 2;
  width: min(520px, 90%);
  margin-left: 8%;
}

h1 {
  font-size: clamp(2.5rem, 5vw, 5rem);
  font-weight: 800;
  color: #222;
  margin-bottom: 20px;
}

p {
  color: #666;
  line-height: 1.9;
  margin-bottom: 35px;
  font-size: 1.05rem;
}

button {
  background: #303c9f;
  color: white;
  border: none;
  padding: 16px 40px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.3s;
}

.arrow {
  position: absolute;
  z-index: 3;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-size: 20px;
  color: #222;
  display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.left {
  left: 25px;
}

.right {
  right: 25px;
}

.dots {
  position: absolute;
  z-index: 3;
  bottom: 35px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
}

.dots span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #bbb;
  cursor: pointer;
  transition: 0.3s;
}

.dots span.active {
  width: 35px;
  border-radius: 20px;
  background: #2ecc71;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .hero {
    height: 70vh;
  }

  .content {
    width: 85%;
    margin: auto;
    text-align: center;
  }

  .arrow {
    display: none;
  }

  h1 {
    font-size: 3rem;
  }

  p {
    font-size: 1rem;
  }

  .overlay {
    background: rgba(255, 255, 255, 0.7);
  }
}

@media (max-width: 600px) {
  .hero {
    height: 60vh;
  }
  p{
    margin: 0;
    line-height: 1.8em;
  }

  h1 {
    font-size: 2rem;
    margin: 23px 0 5px;
  }

  button {
    width: 100%;
    padding: 7px;
  }
  .dots{bottom:5px}
}
</style>

